<script setup>
import {
  markRaw, onMounted, ref, toValue,
} from 'vue';
import { useForm, ErrorMessage } from 'vee-validate';
import * as yup from 'yup';
import {
  pick, pipe, assoc, not, isEmpty, prop,
} from 'ramda';
import { useI18n } from 'vue-i18n';
import InPlaceAlert from '@/components/InPlaceAlert.vue';
import useCallEndPointWith from '@/services/backend/useCallEndPointWith.js';
import { useGlobalState } from '@/store/user.store.js';
import user from '@/services/user.js';
import WithLoader from '@/components/WithLoader.vue';
import { BuildingOffice2Icon, EnvelopeIcon, PhoneIcon } from '@heroicons/vue/24/outline'; 

const { t } = useI18n();
const { isAuthenticated } = useGlobalState();

const validationSchema = markRaw(
  yup.object({
    email: yup.string().email().lowercase().required()
      .label(t('registerView.accountForm.labels.email')),
    confirmEmail: yup.string().email().required().oneOf([yup.ref('email')], t('contact.contactFormV2.validation.emailsMatch'))
      .label(t('registerView.accountForm.labels.confirmEmail')),
    name: yup.string().required().label(t('registerView.accountForm.labels.name')),
    phone: yup.string().label(t('contact.contactFormV2.labels.phone')), 
    message: yup.string().required().max(500).label(t('contact.contactFormV2.labels.message')),
  }),
);

const { defineField, handleSubmit } = useForm({ validationSchema });

const [email, emailAttrs] = defineField('email');
const [confirmEmail, confirmEmailAttrs] = defineField('confirmEmail');
const [name, nameAttrs] = defineField('name');
const [phone, phoneAttrs] = defineField('phone');
const [message, messageAttrs] = defineField('message');

const { callEndpoint, userFriendlyError, isLoading } = useCallEndPointWith('contact');
const createContactFormData = (values) => ({
  email: values.email,
  name: values.name,
  subject: 'Initial Consultation Request', // Default subject
  helpType: 'General', // Default helpType
  message: values.message + (values.phone ? `\n\nPhone: ${values.phone}` : ''),
});

const {
  userInfo,
  fetchUserInfo,
  isLoadingGet,
  errorGet,
} = user();

onMounted(async () => {
  if (isAuthenticated()) {
    await fetchUserInfo();
    if (not(isEmpty(errorGet.value))) {
      console.error('Error retrieving account data:', errorGet.value);
    }
    email.value = prop('email', toValue(userInfo));
    confirmEmail.value = prop('email', toValue(userInfo));
    name.value = `${prop('firstName', toValue(userInfo))} ${prop('lastName', toValue(userInfo))}`;
  }
});
const successMessage = ref('');
const submit = handleSubmit(async (values) => {
  const formData = createContactFormData(values);
  await callEndpoint(assoc('method', 'POST', { request: formData }));
  successMessage.value = t('contact.contactForm.successMessage');
});
</script>

<template>
  <div class="relative isolate mt-12">
    <WithLoader :is-loading="isLoadingGet">
      <WithLoader :is-loading="isLoading">
        <div class="mx-auto grid max-w-7xl grid-cols-1 lg:grid-cols-2 ">
          <!-- Left Column: Info -->
          <div class="relative px-6 pt-24 pb-20 sm:pt-32 lg:static lg:px-8 lg:py-48">
            <div class="mx-auto max-w-xl lg:mx-0 lg:max-w-lg">
              <div class="absolute inset-y-0 left-0 -z-10 w-full overflow-hidden lg:w-1/2">


              </div>
              <h2 class="text-4xl font-semibold tracking-tight text-white sm:text-5xl">
                {{ $t('contact.contactFormV2.header.title') }}
              </h2>

              <p class="mt-6 text-xl text-white font-medium">{{ $t('contact.contactFormV2.header.subtitle') }}</p>
              <ul class="mt-6 space-y-4 text-gray-300 list-disc list-inside">
                <li>{{ $t('contact.contactFormV2.header.list[0]') }}</li>
                <li>{{ $t('contact.contactFormV2.header.list[1]') }}</li>
                <li>{{ $t('contact.contactFormV2.header.list[2]') }}</li>
                <li>{{ $t('contact.contactFormV2.header.list[3]') }}</li>
              </ul>

              <p class="mt-8 text-xl font-semibold text-white">{{ $t('contact.contactFormV2.header.closing') }}</p>

              <dl class="mt-10 space-y-4 text-base/7 text-gray-300">
                <div class="flex gap-x-4">
                  <dt class="flex-none">
                    <span class="sr-only">Telephone</span>
                    <PhoneIcon class="h-7 w-6 text-gray-400" aria-hidden="true" />
                  </dt>
                  <dd>{{ $t('contact.contactFormV2.contactInfo.phone') }} <br /> <a class="hover:text-white"
                      :href="`tel:${$t('contact.contactFormV2.contactInfo.phoneNumber')}`">{{
                        $t('contact.contactFormV2.contactInfo.phoneNumber') }}</a></dd>
                </div>
                <div class="flex gap-x-4">
                  <dt class="flex-none">
                    <span class="sr-only">Email</span>
                    <EnvelopeIcon class="h-7 w-6 text-gray-400" aria-hidden="true" />
                  </dt>
                  <dd>{{ $t('contact.contactFormV2.contactInfo.email') }} <br /> <a class="hover:text-white"
                      :href="`mailto:${$t('contact.contactFormV2.contactInfo.emailAddress')}`">{{
                        $t('contact.contactFormV2.contactInfo.emailAddress') }}</a></dd>
                </div>
                <div class="flex gap-x-4">
                  <dt class="flex-none">
                    <span class="sr-only">Address</span>
                    <BuildingOffice2Icon class="h-7 w-6 text-gray-400" aria-hidden="true" />
                  </dt>
                  <dd v-html="$t('contact.contactFormV2.contactInfo.address')"></dd>
                </div>
              </dl>
            </div>
          </div>

          <!-- Right Column: Form -->
          <div class="px-6 pt-20 pb-24 sm:pb-32 lg:px-8 lg:py-48">
            <form @submit.prevent="submit" class="mx-auto max-w-xl lg:mr-0 lg:max-w-lg">
              <div class="grid grid-cols-1 gap-x-8 gap-y-6 sm:grid-cols-2">
                <InPlaceAlert :message="userFriendlyError" type="error" class="col-span-1 sm:col-span-2"
                  @close-alert="userFriendlyError = ''" />
                <InPlaceAlert v-if="successMessage" :message="successMessage" type="success"
                  class="col-span-1 sm:col-span-2" @close-alert="successMessage = ''" />

                <div class="sm:col-span-2">
                  <label for="name" class="block text-sm/6 font-semibold text-white">{{
                    $t('contact.contactFormV2.labels.name') }}</label>
                  <div class="mt-2.5">
                    <input type="text" id="name" v-model="name" v-bind="nameAttrs" autocomplete="name"
                      class="block w-full rounded-md bg-white/5 px-3.5 py-2 text-base text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500" />
                    <ErrorMessage name="name" class="mt-1 text-sm text-red-400" as="p" />
                  </div>
                </div>
                <div class="sm:col-span-2">
                  <label for="email" class="block text-sm/6 font-semibold text-white">{{
                    $t('contact.contactFormV2.labels.email') }}</label>
                  <div class="mt-2.5">
                    <input type="email" id="email" v-model="email" v-bind="emailAttrs" autocomplete="email"
                      class="block w-full rounded-md bg-white/5 px-3.5 py-2 text-base text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500" />
                    <ErrorMessage name="email" class="mt-1 text-sm text-red-400" as="p" />
                  </div>
                </div>
                <div class="sm:col-span-2">
                  <label for="confirmEmail" class="block text-sm/6 font-semibold text-white">{{
                    $t('contact.contactFormV2.labels.confirmEmail') }}</label>
                  <div class="mt-2.5">
                    <input type="email" id="confirmEmail" v-model="confirmEmail" v-bind="confirmEmailAttrs"
                      autocomplete="email"
                      class="block w-full rounded-md bg-white/5 px-3.5 py-2 text-base text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500" />
                    <ErrorMessage name="confirmEmail" class="mt-1 text-sm text-red-400" as="p" />
                  </div>
                </div>

                <div class="sm:col-span-2">
                  <label for="phone" class="block text-sm/6 font-semibold text-white">{{
                    $t('contact.contactFormV2.labels.phone') }}</label>
                  <div class="mt-2.5">
                    <input type="tel" id="phone" v-model="phone" v-bind="phoneAttrs" autocomplete="tel"
                      class="block w-full rounded-md bg-white/5 px-3.5 py-2 text-base text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500" />
                    <ErrorMessage name="phone" class="mt-1 text-sm text-red-400" as="p" />
                  </div>
                </div>

                <div class="sm:col-span-2">
                  <label for="message" class="block text-sm/6 font-semibold text-white">{{
                    $t('contact.contactFormV2.labels.message') }}</label>
                  <div class="mt-2.5">
                    <textarea id="message" v-model="message" v-bind="messageAttrs" rows="4"
                      class="block w-full rounded-md bg-white/5 px-3.5 py-2 text-base text-white outline-1 -outline-offset-1 outline-white/10 placeholder:text-gray-500 focus:outline-2 focus:-outline-offset-2 focus:outline-indigo-500"></textarea>
                    <ErrorMessage name="message" class="mt-1 text-sm text-red-400" as="p" />
                  </div>
                </div>
              </div>
              <div class="mt-8 flex justify-end">
                <button type="submit"
                  class="rounded-md bg-terciary px-3.5 py-2.5 text-center text-sm font-semibold text-white shadow-xs hover:bg-teal-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500">
                  {{ $t('contact.contactFormV2.submit') }}
                </button>
              </div>
            </form>
          </div>
        </div>
      </WithLoader>
    </WithLoader>
  </div>
</template>